import { ref, computed, watch, onMounted } from 'vue';

/**
 * usePlacesAutocomplete composable
 * Encapsulates Google Places Autocomplete logic with dynamic script loading.
 *
 * Options:
 *  - apiKey: Google Maps API Key (required for autocomplete; if missing, composable degrades gracefully)
 *  - country: ISO 2 country code restriction (default 'ca')
 *  - fallback: Fallback query for map embed when address empty (default 'Canada')
 *  - minChars: Minimum characters before querying predictions (default 3)
 *  - debounceMs: Debounce delay for predictions fetch (default 300)
 *  - zoomWhenEmpty: Zoom level applied when address empty (default 4)
 *
 * Returns refs:
 *  - address
 *  - predictions
 *  - loading
 *  - show
 *  - select(prediction)
 *  - handleBlur()
 *  - mapEmbedUrl (computed)
 *
 * This composable is safe to mount multiple times; script injected once.
 */
export function usePlacesAutocomplete({
    apiKey,
    country = 'ca',
    fallback = 'Canada',
    minChars = 3,
    debounceMs = 300,
    zoomWhenEmpty = 4,
} = {}) {
    const address = ref('');
    const predictions = ref([]); // [{ description, place_id }]
    const loading = ref(false);
    const show = ref(false);
    const googleLoaded = ref(false);
    let service = null;
    let debounceTimer = null;

    const scriptId = 'google-maps-js';
    const callbackName = '__gmPlacesInit';

    const debounce = (fn, delay = debounceMs) => {
        return (...args) => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
            debounceTimer = setTimeout(() => fn(...args), delay);
        };
    };

    const initServiceIfReady = () => {
        if (window.google?.maps?.places && !service) {
            service = new window.google.maps.places.AutocompleteService();
        }
    };

    const loadScript = () => {
        if (typeof window === 'undefined') {
            return;
        }
        if (window.google?.maps?.places) {
            googleLoaded.value = true;
            initServiceIfReady();
            return;
        }
        if (!apiKey) {
            // Without API key we cannot use predictions; silently skip.
            return;
        }
        const existing = document.getElementById(scriptId);
        if (existing) {
            existing.addEventListener('load', () => {
                googleLoaded.value = true;
                initServiceIfReady();
            });
            return;
        }
        // Inject script
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=${callbackName}`;
        script.async = true;
        script.defer = true;
        window[callbackName] = () => {
            googleLoaded.value = true;
            initServiceIfReady();
        };
        document.head.appendChild(script);
    };

    const fetchPredictions = debounce((input) => {
        if (!service || !input || input.length < minChars) {
            predictions.value = [];
            return;
        }
        loading.value = true;
        service.getPlacePredictions({ input, componentRestrictions: { country } }, (preds, status) => {
            loading.value = false;
            if (status !== window.google.maps.places.PlacesServiceStatus.OK || !preds) {
                predictions.value = [];
                return;
            }
            predictions.value = preds.map(p => ({ description: p.description, place_id: p.place_id }));
        });
    });

    watch(address, (val) => {
        show.value = !!val && val.length >= minChars;
        fetchPredictions(val);
    });

    const select = (prediction) => {
        address.value = prediction.description;
        show.value = false;
        predictions.value = [];
    };

    const handleBlur = () => {
        setTimeout(() => {
            show.value = false;
        }, 150);
    };

    const mapEmbedUrl = computed(() => {
        const hasAddress = !!address.value?.trim().length;
        const querySource = hasAddress ? address.value.trim() : fallback;
        const base = `https://www.google.com/maps?q=${encodeURIComponent(querySource)}`;
        const zoomPart = !hasAddress ? `&z=${zoomWhenEmpty}` : '';
        return `${base}${zoomPart}&output=embed`;
    });

    onMounted(() => {
        loadScript();
    });

    return {
        address,
        predictions,
        loading,
        show,
        select,
        handleBlur,
        mapEmbedUrl,
        googleLoaded,
    };
}

export default usePlacesAutocomplete;
